'use es6';

import { RequestEntryRecord, RequestStatusRecord } from 'AdsUI/records/RequestStatus';
import CampaignFilterRecord from 'AdsUI/records/reporting/CampaignFilterRecord';
import { Map as ImmutableMap, Record } from 'immutable';
import KeyMirror from 'react-utils/keyMirror';
import { convertTimeRangeToKey } from 'AdsUI/lib/Utils';
const fetchStarted = {
  fetching: true,
  fetchFailed: false
};
const fetchSucceeded = {
  fetching: false,
  fetched: true,
  fetchFailed: false
};
const fetchFailed = {
  fetching: false,
  fetched: true,
  fetchFailed: true
};
const syncStarted = {
  syncing: true,
  syncFailed: false
};
const syncSucceeded = {
  syncing: false,
  synced: true,
  syncFailed: false
};
const syncFailed = {
  syncing: false,
  synced: true,
  syncFailed: true
};
const invalidated = {
  fetching: false,
  fetched: false,
  syncing: false,
  synced: false,
  fetchFailed: false,
  syncFailed: false
};
const RequestTypes = KeyMirror({
  SYNC: null,
  FETCH: null,
  INVALIDATE_RECORD: null,
  INVALIDATE_MAP: null
});

const massInvalidate = (keys, path, requestType) => keys.map(key => new RequestEntryRecord({
  actionStem: key,
  requestType,
  path
})); // 1. Add your request type to the record here


const initialState = new Record({
  adAccountsOverview: new RequestStatusRecord({}),
  connectedUsers: new RequestStatusRecord({}),
  campaignDefaults: new RequestStatusRecord({}),
  campaignTips: new RequestStatusRecord({}),
  newAdWordsCampaignEntity: new RequestStatusRecord({}),
  adWordsCampaignEntities: ImmutableMap(),
  adWordsAdGroupShoppingPartitions: ImmutableMap(),
  adWordsTrackingTemplate: new RequestStatusRecord({}),
  adWordsConversionTrackerEntities: new RequestStatusRecord({}),
  adWordsKeywordUpdate: new RequestStatusRecord({}),
  adWordsChildrenBiddingStrategies: ImmutableMap(),
  adWordsAdCreate: new RequestStatusRecord({}),
  locationExtensions: new RequestStatusRecord({}),
  adExtensions: new RequestStatusRecord({}),
  dealsCurrencySymbol: new RequestStatusRecord({}),
  adPreviews: new ImmutableMap(),
  fbAdsThumbnails: new ImmutableMap(),
  fbAdCreative: new RequestStatusRecord({}),
  contacts: new ImmutableMap(),
  fbBreakdownFunnels: new ImmutableMap(),
  audiences: new RequestStatusRecord({}),
  inactiveAudiences: new RequestStatusRecord({}),
  pixels: new RequestStatusRecord({}),
  pixelCreates: new RequestStatusRecord({}),
  pixelInstalls: new RequestStatusRecord({}),
  reportingCampaignDetails: new ImmutableMap(),
  reportingChildren: new ImmutableMap(),
  adSets: new ImmutableMap(),
  tableColumnOrder: new RequestStatusRecord({}),
  accountsWithUnacceptedToS: new RequestStatusRecord({}),
  events: new RequestStatusRecord({}),
  campaignList: new ImmutableMap(),
  campaignListEarlyRequest: new ImmutableMap(),
  sequenceList: new ImmutableMap(),
  audiencesUsageInfo: new RequestStatusRecord({}),
  supportedAudiences: new ImmutableMap(),
  savedAudiences: new ImmutableMap(),
  leadForms: new ImmutableMap(),
  notificationEmails: new ImmutableMap(),
  fbGeolocationsSearch: new ImmutableMap(),
  networkAggregates: new RequestStatusRecord(),
  leadSyncingEntities: new ImmutableMap(),
  userAttributes: new RequestStatusRecord(),
  leadSyncSelected: new RequestStatusRecord(),
  campaignInfo: new ImmutableMap(),
  coupons: new RequestStatusRecord(),
  couponReveal: new ImmutableMap(),
  hubAttributes: new RequestStatusRecord(),
  estimatedSize: new RequestStatusRecord(),
  drafts: new RequestStatusRecord(),
  hsProperties: new RequestStatusRecord(),
  consentProperties: new RequestStatusRecord(),
  myPermissions: new RequestStatusRecord(),
  allPermissions: new RequestStatusRecord(),
  pages: new ImmutableMap(),
  users: new RequestStatusRecord(),
  existingAds: new ImmutableMap(),
  existingKeywords: new ImmutableMap(),
  membershipCounts: new ImmutableMap(),
  sequenceDetails: new ImmutableMap(),
  usersInfo: new RequestStatusRecord(),
  teamsInfo: new RequestStatusRecord(),
  lifecycleStages: new RequestStatusRecord(),
  pixelPageViewEvents: new RequestStatusRecord(),
  automationContactLists: new RequestStatusRecord(),
  automationWorkflows: new RequestStatusRecord()
})({}); // 2. and here

const requestEntries = [new RequestEntryRecord({
  actionStem: 'AD_ACCOUNTS_OVERVIEW_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['adAccountsOverview']
}), new RequestEntryRecord({
  actionStem: 'CONNECTED_USERS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['connectedUsers']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_CAMPAIGN_ENTITY_FETCH',
  requestType: RequestTypes.FETCH,
  path: payload => ['adWordsCampaignEntities', payload.campaignId]
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_CAMPAIGN_STATE_INVALIDATE',
  requestType: RequestTypes.INVALIDATE_RECORD,
  path: payload => ['adWordsCampaignEntities', payload.campaignId]
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_SHOPPING_PARTITION_ENTITIES_FETCH',
  requestType: RequestTypes.FETCH,
  path: payload => ['adWordsAdGroupShoppingPartitions', payload.adGroupId]
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_TRACKING_TEMPLATE_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['adWordsTrackingTemplate']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_TRACKING_TEMPLATE_UPDATE',
  requestType: RequestTypes.SYNC,
  path: () => ['adWordsTrackingTemplate']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_KEYWORD_UPDATE',
  requestType: RequestTypes.SYNC,
  path: () => ['adWordsKeywordUpdate']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_CONVERSION_TRACKERS_SYNC',
  requestType: RequestTypes.SYNC,
  path: () => ['adWordsConversionTrackerEntities']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_CONVERSION_TRACKERS_SAVE',
  requestType: RequestTypes.SYNC,
  path: () => ['adWordsConversionTrackerEntities']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_CONVERSION_TRACKERS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['adWordsConversionTrackerEntities']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_CAMPAIGN_CHILDREN_BIDDING_STRATEGIES_CLEAR',
  requestType: RequestTypes.SYNC,
  path: () => ['adWordsChildrenBiddingStrategies']
}), new RequestEntryRecord({
  actionStem: 'ADWORDS_AD_CREATE',
  requestType: RequestTypes.SYNC,
  path: () => ['adWordsAdCreate']
}), new RequestEntryRecord({
  actionStem: 'AD_LOCATION_EXTENSION_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['locationExtensions']
}), new RequestEntryRecord({
  actionStem: 'AD_EXTENSION_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['adExtensions']
}), new RequestEntryRecord({
  actionStem: 'DEALS_CURRENCY_SYMBOL_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['dealsCurrencySymbol']
}), new RequestEntryRecord({
  actionStem: 'AD_PREVIEW_FETCH',
  requestType: RequestTypes.FETCH,
  path: payload => ['adPreviews', payload.network, payload.groupId, payload.adId]
}), new RequestEntryRecord({
  actionStem: 'FB_ADS_THUMBNAILS_FETCH',
  requestType: RequestTypes.FETCH,
  path: payload => ['fbAdsThumbnails', payload.adSetId]
}), new RequestEntryRecord({
  actionStem: 'FB_AD_CREATIVE_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['fbAdCreative']
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_LIST_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    filters
  }) => ['campaignList', filters]
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_LIST_STATE_INVALIDATE',
  requestType: RequestTypes.INVALIDATE_RECORD,
  path: ({
    filters
  }) => ['campaignList', filters]
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_LIST_STATE_INVALIDATE',
  requestType: RequestTypes.INVALIDATE_RECORD,
  path: ({
    filters
  }) => ['campaignListEarlyRequest', filters]
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_LIST_EARLY_REQUEST_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    filters
  }) => ['campaignListEarlyRequest', filters]
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_LIST_RESET',
  requestType: RequestTypes.INVALIDATE_MAP,
  path: () => ['campaignList']
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_REPORTING_ENTITY_UPDATE_FAILED',
  requestType: RequestTypes.INVALIDATE_MAP,
  path: () => ['campaignList']
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_LIST_RESET',
  requestType: RequestTypes.INVALIDATE_MAP,
  path: () => ['campaignListEarlyRequest']
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_REPORTING_ENTITY_UPDATE_FAILED',
  requestType: RequestTypes.INVALIDATE_MAP,
  path: () => ['campaignListEarlyRequest']
}), new RequestEntryRecord({
  actionStem: 'SEQUENCE_LIST_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    filters
  }) => ['sequenceList', filters]
}), new RequestEntryRecord({
  actionStem: 'AUDIENCES_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['audiences']
}), new RequestEntryRecord({
  actionStem: 'AUDIENCES_INACTIVE_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['inactiveAudiences']
}), new RequestEntryRecord({
  actionStem: 'AUDIENCES_RESET',
  requestType: RequestTypes.RESET,
  path: () => ['audiences']
}), new RequestEntryRecord({
  actionStem: 'ACCOUNTS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['accounts']
}), new RequestEntryRecord({
  actionStem: 'FB_BREAKDOWN_FUNNELS_FETCH',
  requestType: RequestTypes.FETCH,
  path: payload => ['fbBreakdownFunnels', payload.campaignId]
}), new RequestEntryRecord({
  actionStem: 'PIXELS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['pixels']
}), new RequestEntryRecord({
  actionStem: 'PIXEL_CREATE',
  requestType: RequestTypes.SYNC,
  path: () => ['pixelCreates']
}), new RequestEntryRecord({
  actionStem: 'PIXEL_INSTALL',
  requestType: RequestTypes.SYNC,
  path: () => ['pixelInstalls']
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_DETAILS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    filters
  }) => ['reportingCampaignDetails', CampaignFilterRecord(filters)]
}), ...massInvalidate(['ADWORDS_ADGROUP_UPDATE_SUCCEEDED', 'ADWORDS_ADGROUP_DELETE_SUCCEEDED', 'ADWORDS_ADGROUP_CREATE_SUCCEEDED', 'ADWORDS_AD_UPDATE_SUCCEEDED', 'ADWORDS_AD_CREATE_SUCCEEDED', 'ADWORDS_KEYWORD_UPDATE_SUCCEEDED', 'KEYWORD_CREATE_SUCCEEDED', 'KEYWORD_BULK_CREATE_SUCCEEDED', 'ENTITY_DELETE_SUCCEEDED', 'CHILD_ENTITY_FETCH_RESET'], () => ['reportingChildren'], RequestTypes.INVALIDATE_MAP), new RequestEntryRecord({
  actionStem: 'CHILDREN_BY_ENTITY_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    filters
  }) => ['reportingChildren', CampaignFilterRecord(filters)]
}), new RequestEntryRecord({
  actionStem: 'COLUMN_ORDER_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['tableColumnOrder']
}), new RequestEntryRecord({
  actionStem: 'FB_ACCOUNTS_WITH_UNACCEPTED_TOS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['accountsWithUnacceptedToS']
}), new RequestEntryRecord({
  actionStem: 'EVENTS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['events']
}), new RequestEntryRecord({
  actionStem: 'AUDIENCES_USAGE_INFO_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['audiencesUsageInfo']
}), new RequestEntryRecord({
  actionStem: 'SUPPORTED_AUDIENCES_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    adAccount = {}
  }) => ['supportedAudiences', adAccount.network, adAccount.accountId]
}), new RequestEntryRecord({
  actionStem: 'SAVED_AUDIENCES_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    adAccount = {}
  }) => ['savedAudiences', adAccount.network, adAccount.accountId]
}), new RequestEntryRecord({
  actionStem: 'LEAD_FORMS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    path = []
  }) => ['leadForms', ...path]
}), new RequestEntryRecord({
  actionStem: 'LEAD_AD_NOTIFICATION_EMAILS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    network,
    id
  }) => ['notificationEmails', network, id]
}), new RequestEntryRecord({
  actionStem: 'FB_GEOLOCATIONS_SEARCH',
  requestType: RequestTypes.FETCH,
  path: ({
    searchTerm
  }) => ['fbGeolocationsSearch', searchTerm]
}), new RequestEntryRecord({
  actionStem: 'NETWORK_AGGREGATE_DATA_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['networkAggregates']
}), new RequestEntryRecord({
  actionStem: 'LEAD_AD_FETCH_LEAD_SYNCING_ENTITIES',
  requestType: RequestTypes.FETCH,
  path: ({
    accountId,
    network
  }) => ['leadSyncingEntities', network, accountId]
}), new RequestEntryRecord({
  actionStem: 'USER_ATTRIBUTES_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['userAttributes']
}), new RequestEntryRecord({
  actionStem: 'LEAD_SYNCING_SELECTED_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['leadSyncSelected']
}), new RequestEntryRecord({
  actionStem: 'CAMPAIGN_INFO_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    network,
    accountId
  }) => ['campaignInfo', network, accountId]
}), new RequestEntryRecord({
  actionStem: 'COUPONS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['coupons']
}), new RequestEntryRecord({
  actionStem: 'COUPON_REVEAL',
  requestType: RequestTypes.SYNC,
  path: ({
    network
  }) => ['couponReveal', network]
}), new RequestEntryRecord({
  actionStem: 'HUB_ATTRIBUTES_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['hubAttributes']
}), new RequestEntryRecord({
  actionStem: 'ESTIMATED_SIZE_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['estimatedSize']
}), ...massInvalidate(['LEAD_AD_CREATION_UPDATE_FORM', 'LEAD_AD_CREATION_UPDATE_TARGETING'], () => ['estimatedSize'], RequestTypes.INVALIDATE_RECORD), new RequestEntryRecord({
  actionStem: 'DRAFTS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['drafts']
}), new RequestEntryRecord({
  actionStem: 'HS_PROPERTIES_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['hsProperties']
}), new RequestEntryRecord({
  actionStem: 'CONSENT_PROPERTIES_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['consentProperties']
}), new RequestEntryRecord({
  actionStem: 'AD_ACCOUNTS_MY_PERMISSIONS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['myPermissions']
}), new RequestEntryRecord({
  actionStem: 'ALL_PERMISSIONS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['allPermissions']
}), new RequestEntryRecord({
  actionStem: 'PAGES_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    network,
    accountId
  }) => ['pages', network, accountId]
}), new RequestEntryRecord({
  actionStem: 'USERS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['users']
}), new RequestEntryRecord({
  actionStem: 'EXISTING_ADS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    network,
    accountId,
    campaignId,
    adGroupId
  }) => ['existingAds', network, accountId, campaignId, adGroupId]
}), new RequestEntryRecord({
  actionStem: 'EXISTING_KEYWORDS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    network,
    accountId,
    campaignId,
    adGroupId
  }) => ['existingKeywords', network, accountId, campaignId, adGroupId]
}), new RequestEntryRecord({
  actionStem: 'MEMBERSHIP_COUNTS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    contactListId
  }) => ['membershipCounts', contactListId]
}), new RequestEntryRecord({
  actionStem: 'SEQUENCE_DETAILS_FETCH',
  requestType: RequestTypes.FETCH,
  path: ({
    filters: {
      timeRange,
      sequenceId,
      attributionView
    } = {}
  } = {}) => ['sequenceDetails', sequenceId, timeRange && convertTimeRangeToKey(timeRange), attributionView]
}), new RequestEntryRecord({
  actionStem: 'USERS_INFO_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['usersInfo']
}), new RequestEntryRecord({
  actionStem: 'TEAMS_INFO_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['teamsInfo']
}), new RequestEntryRecord({
  actionStem: 'LIFECYCLE_STAGES_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['lifecycleStages']
}), new RequestEntryRecord({
  actionStem: 'PIXELS_EVENT_COUNT_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['pixelPageViewEvents']
}), new RequestEntryRecord({
  actionStem: 'AUTOMATION_CONTACT_LIST_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['automationContactLists']
}), new RequestEntryRecord({
  actionStem: 'AUTOMATION_ALL_WORKFLOWS_FETCH',
  requestType: RequestTypes.FETCH,
  path: () => ['automationWorkflows']
})]; // For testing purposes

window.__requestEntries = requestEntries;

const getUpdateObject = (requestType, actionType) => {
  const requestStage = actionType.substring(actionType.lastIndexOf('_') + 1);

  if (requestType === RequestTypes.SYNC) {
    const syncMap = {
      START: syncStarted,
      SUCCEEDED: syncSucceeded,
      FAILED: syncFailed
    };
    return syncMap[requestStage];
  } else if (requestType === RequestTypes.FETCH) {
    const fetchMap = {
      START: fetchStarted,
      SUCCEEDED: fetchSucceeded,
      FAILED: fetchFailed
    };
    return fetchMap[requestStage];
  } else if (requestType === RequestTypes.INVALIDATE_RECORD) {
    return invalidated;
  } else if (requestType === RequestTypes.INVALIDATE_MAP) {
    return ImmutableMap();
  }

  throw new Error(`Unknown requestType ${requestType}`);
};

const updateRequestStatus = (requestType, state, path, newStatus) => {
  if (requestType === RequestTypes.INVALIDATE_MAP) {
    return state.setIn(path, newStatus);
  } else {
    const reqStatus = state.getIn(path);

    if (reqStatus) {
      return state.mergeIn(path, reqStatus.merge(newStatus));
    } else {
      return state.setIn(path, new RequestStatusRecord(newStatus));
    }
  }
};

export default ((state = initialState, {
  type,
  payload
}) => requestEntries.filter(entry => {
  return type.indexOf(entry.actionStem) === 0;
}).reduce((newState, entry) => {
  const updateObject = getUpdateObject(entry.requestType, type);
  const path = entry.path(payload || {});
  return updateRequestStatus(entry.requestType, newState, path, updateObject);
}, state));